import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';
import FAQSection from '../../components/FAQSection';

const BatwaTrail = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const faqs = [
    { question: 'Do the Batwa guides speak English?', answer: 'Most Batwa guides speak some English, and there is typically an interpreter available to help with complex cultural concepts and stories. The combination of demonstration, gesture, and translation ensures you understand the experiences being shared even if language barriers exist.' },
    { question: 'Is this experience appropriate for children?', answer: 'Yes, children aged 8+ usually find it fascinating, especially the hunting demonstrations and fire-making. Younger children may struggle with the 4-5 hour duration and moderate hiking. The content is educational and appropriate for all ages.' },
    { question: 'How does my participation help the Batwa community?', answer: 'The $80 trail fee goes directly to the Batwa community, funding healthcare clinics, school fees for children, income for guides, and cultural preservation projects. This is one of the few sustainable income sources for displaced Batwa families, making your participation genuinely impactful.' },
    { question: 'Can I purchase Batwa crafts?', answer: 'Yes! At the end of the trail, community members often have handmade baskets, bows, jewelry, and other crafts for sale. Purchasing directly supports artisans and their families. Bargaining is generally not appropriate—these are fair prices supporting livelihoods.' },
    { question: 'Is photography allowed?', answer: 'Photography is generally permitted, but always ask permission before photographing individuals. Some Batwa appreciate photos as a way to share their culture, while others may be uncomfortable. Respectful communication is key. Flash photography should be avoided in the sacred cave.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Batwa Cultural Trail Experience
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Walk with the forest's original keepers and learn ancient survival wisdom in Bwindi
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(203 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Authentic Indigenous Culture
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                Community Benefits Batwa
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Cultural Experience
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Step Into Living History with Uganda's Forest People
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Long before national parks and conservation areas existed, the Batwa people called Bwindi Impenetrable Forest home for thousands of years. These diminutive hunter-gatherers knew every medicinal plant, every animal track, and every secret the forest held—surviving and thriving without modern tools or technology.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Batwa Cultural Trail offers you a rare opportunity to walk alongside these original forest dwellers as they share survival skills, creation stories, hunting techniques, and spiritual traditions that have been passed down through countless generations. This is not a museum exhibit or theatrical performance; it is a deeply authentic encounter with living culture, led by actual Batwa community members in the very forest that shaped their identity.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your experience begins at the trail entrance where you will meet your Batwa guides. As you enter the forest together, the transformation is immediate—the Batwa move with an ease and confidence that makes you realize you are in the presence of true experts. They point out plants that look identical to your untrained eye but are entirely different: this one treats malaria, that one soothes stomach ailments, this other prevents infections.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Ancient Skills and Sacred Sites</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  You will watch in amazement as they demonstrate fire-making using bamboo friction, create rope from bark with stunning speed, and explain how they constructed temporary shelters. The hunting demonstration is particularly fascinating—showing how they crafted bows from specific woods and arrows from bamboo.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The trail culminates at a sacred cave that once served as a royal dwelling place for the Batwa king. Inside this cool, moss-covered space, your guides perform a ceremonial blessing and share their hopes for preserving Batwa culture. The $80 trail fee directly supports the Batwa community, funding healthcare, education, and income for guides.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                What Makes This Special
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-users', title: 'Authentic Batwa Guides', description: 'Led by actual Batwa community members who lived in the forest before relocation' },
                  { icon: 'fa-leaf', title: 'Ancient Survival Skills', description: 'Learn fire-making, plant medicine, hunting techniques, and shelter construction' },
                  { icon: 'fa-music', title: 'Traditional Songs & Stories', description: 'Experience haunting harmonies and creation myths passed down for generations' },
                  { icon: 'fa-bullseye', title: 'Hunting Demonstrations', description: 'See bow-and-arrow craftsmanship and tracking skills honed over millennia' },
                  { icon: 'fa-dungeon', title: 'Sacred Cave Visit', description: 'Enter the ancient royal dwelling place with ceremonial blessings' },
                  { icon: 'fa-heart', title: 'Direct Community Benefit', description: 'All trail fees support Batwa healthcare, education, and economic empowerment' },
                  { icon: 'fa-book', title: 'Educational & Moving', description: 'Gain deep insights into both forest ecology and human displacement impacts' },
                  { icon: 'fa-camera', title: 'Photography Opportunities', description: 'Capture demonstrations and portraits (with respectful permission)' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Cultural Journey
              </h2>
              <div className="space-y-6">
                {[
                  { time: '08:00 AM', title: 'Trail Entrance Meeting', desc: 'Meet your Batwa guides at trailhead, introductions and cultural context briefing' },
                  { time: '08:30 AM', title: 'Forest Entry & Plant Medicine', desc: 'Enter Bwindi forest, learn to identify medicinal plants and their traditional uses' },
                  { time: '09:30 AM', title: 'Survival Skills Demos', desc: 'Watch fire-making, rope-crafting, and shelter construction techniques' },
                  { time: '10:30 AM', title: 'Hunting Techniques', desc: 'Learn bow-and-arrow making, tracking skills, and hunting strategies demonstration' },
                  { time: '11:30 AM', title: 'Sacred Cave Visit', desc: 'Enter the ancient royal cave with ceremonial blessings and historical stories' },
                  { time: '12:00 PM', title: 'Songs & Storytelling', desc: 'Traditional Batwa harmonies, creation myths, and personal displacement narratives' },
                  { time: '12:45 PM', title: 'Q&A and Farewell', desc: 'Ask questions, share reflections, purchase crafts directly from community members' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Common Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Trail Experience
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>Note:</strong> This itinerary is tentative and can be customized based on your preferences.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">The ultimate primate encounter in Bwindi.</p>
                    <p className="text-safari-gold font-bold">From $800 per person</p>
                  </div>
                </Link>

                <Link to="/activities/gorilla-habituation" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800"
                      alt="Gorilla Habituation"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Habituation</h3>
                    <p className="text-gray-700 mb-4">Spend 4 hours with researchers and gorillas.</p>
                    <p className="text-safari-gold font-bold">From $1,500 per person</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 4-5 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Price:</strong> $80 per person</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 8 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Experience
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Batwa community guides",
                    "Cultural demonstrations",
                    "Forest trail access",
                    "Sacred cave visit",
                    "Traditional performances",
                    "Community development fee",
                    "Park entrance contribution"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to trailhead",
                    "Meals or refreshments",
                    "Craft purchases (optional)",
                    "Tips for guides",
                    "Accommodation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. 4-5km hiking on uneven terrain.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Clothing:</strong> Wear hiking boots and rain gear.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Batwa Cultural Trail Experience"
      />
    </div>
  );
};

export default BatwaTrail;
